/**
 * src/components/settlement/DebtMinimizerSheet.tsx
 * Ultra-Premium Apple Sheet for Visual Multi-Party Debt Settlements
 */

import React, { useMemo, useState } from 'react';
import { GlassModal } from '../ui/GlassModal';
import { api, type UserItem } from '../../services/api';
import { minimizeDebts, type SettlementTransfer } from '../../utils/debtMinimizer';
import { ArrowRight, Sparkles, CheckCircle2, Send, QrCode, Bell } from 'lucide-react';
import { generateWhatsAppReminderUrl } from '../../utils/whatsapp';
import { useToast } from '../../context/ToastContext';
import { haptic, playSuccessSound, playTapSound } from '../../utils/audioHaptics';

interface DebtMinimizerSheetProps {
  isOpen: boolean;
  onClose: () => void;
  users: UserItem[];
  onInitiateSettlement?: (transfer: SettlementTransfer) => void;
}

export const DebtMinimizerSheet: React.FC<DebtMinimizerSheetProps> = ({
  isOpen,
  onClose,
  users,
  onInitiateSettlement
}) => {
  const transfers = useMemo(() => minimizeDebts(users), [users]);

  const [sendingUserId, setSendingUserId] = useState<number | null>(null);
  const { showToast } = useToast();

  const handleSendInAppReminder = async (item: SettlementTransfer) => {
    if (sendingUserId === item.fromUser.id) return;
    haptic.tap();
    playTapSound();
    setSendingUserId(item.fromUser.id);
    try {
      await api.notifications.remind({
        target_user_id: item.fromUser.id,
        amount: item.amount
      });
      haptic.success();
      playSuccessSound();
      showToast(`In-app push reminder sent to ${item.fromUser.full_name || item.fromUser.username}! They will receive it in their PWA.`, 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to send in-app reminder', 'error');
    } finally {
      setSendingUserId(null);
    }
  };

  const totalOwed = useMemo(() => {
    return transfers.reduce((sum, t) => sum + t.amount, 0);
  }, [transfers]);

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title="Smart Debt Settlement Matrix"
      maxWidth="540px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Apple Card Optimization Banner */}
        <div
          className="apple-card"
          style={{
            padding: '20px',
            borderRadius: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}
        >
          <div
            className="ios-icon-squircle blue"
            style={{ width: '48px', height: '48px', borderRadius: '14px', flexShrink: 0 }}
          >
            <Sparkles size={24} />
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-tertiary)', fontWeight: 700 }}>
              Algorithmic Debt Optimization
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.02em', marginTop: '2px' }}>
              {transfers.length > 0 ? `${transfers.length} Direct Payments to Settle All` : 'All Accounts Balanced'}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--ios-blue)', fontWeight: 600, marginTop: '2px' }}>
              Total Volume: ₹{totalOwed.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        {/* Transfers Inset Grouped List */}
        <div>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-tertiary)', paddingLeft: '14px', marginBottom: '8px' }}>
            Suggested Cash Transfers ({transfers.length})
          </div>

          <div className="ios-inset-group" style={{ margin: 0 }}>
            {transfers.length === 0 ? (
              <div style={{ padding: '36px 20px', textAlign: 'center' }}>
                <CheckCircle2 size={32} color="var(--ios-green)" style={{ margin: '0 auto 8px' }} />
                <div style={{ fontSize: '0.98rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  Zero Net Debt
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Every member in the ledger is currently balanced.
                </div>
              </div>
            ) : (
              transfers.map((item, idx) => {
                const toUserUpi = item.toUser.upi_id || (item.toUser.role === 'admin' ? 'chintankheradia123-1@okaxis' : null);
                return (
                  <div
                    key={item.id}
                    className="ios-list-row"
                    style={{
                      padding: '14px 18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: idx < transfers.length - 1 ? 'var(--border-hairline)' : 'none',
                      gap: '12px'
                    }}
                  >
                    {/* Left: From -> To visualization */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: 1 }}>
                      {/* Debtor Squircle Avatar */}
                      <div
                        className="ios-icon-squircle red"
                        style={{ width: '38px', height: '38px', borderRadius: '11px', fontSize: '0.84rem', fontWeight: 700, flexShrink: 0 }}
                      >
                        {item.fromUser.avatar_url ? (
                          <img src={item.fromUser.avatar_url} alt={item.fromUser.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          (item.fromUser.full_name || item.fromUser.username).slice(0, 2).toUpperCase()
                        )}
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                            {item.fromUser.full_name || item.fromUser.username}
                          </span>
                          <ArrowRight size={13} color="var(--text-tertiary)" />
                          <span style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--ios-green)' }}>
                            {item.toUser.full_name || item.toUser.username}
                          </span>
                        </div>

                        {/* Recipient Google Pay UPI ID Badge */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#4285F4' }}>Pay GPay:</span>
                          <span style={{
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: toUserUpi ? 'var(--text-secondary)' : 'var(--ios-red)',
                            background: toUserUpi ? 'rgba(66, 133, 244, 0.1)' : 'rgba(255, 69, 58, 0.12)',
                            padding: '1px 6px',
                            borderRadius: '5px'
                          }}>
                            {toUserUpi || 'No UPI ID Added'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right: Amount & Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                          ₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>

                      {/* 1-Tap Google Pay Settle Action */}
                      {onInitiateSettlement ? (
                        <button
                          type="button"
                          onClick={() => onInitiateSettlement(item)}
                          className="ios-tap-target"
                          style={{
                            padding: '7px 11px',
                            borderRadius: '10px',
                            background: 'rgba(66, 133, 244, 0.14)',
                            border: '1px solid rgba(66, 133, 244, 0.3)',
                            color: '#4285F4',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            cursor: 'pointer'
                          }}
                          title={`Pay ₹${item.amount.toFixed(2)} to ${item.toUser.username} via Google Pay`}
                        >
                          <QrCode size={13} />
                          <span>Pay GPay</span>
                        </button>
                      ) : toUserUpi ? (
                        <a
                          href={`tez://upi/pay?pa=${encodeURIComponent(toUserUpi)}&pn=${encodeURIComponent(item.toUser.username)}&am=${item.amount.toFixed(2)}&cu=INR&tn=${encodeURIComponent(`CKAudit: ${item.fromUser.username} to ${item.toUser.username}`)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="ios-tap-target"
                          style={{
                            padding: '7px 11px',
                            borderRadius: '10px',
                            background: 'rgba(66, 133, 244, 0.14)',
                            border: '1px solid rgba(66, 133, 244, 0.3)',
                            color: '#4285F4',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            textDecoration: 'none'
                          }}
                          title={`Launch Google Pay for ₹${item.amount.toFixed(2)}`}
                        >
                          <QrCode size={13} />
                          <span>Pay GPay</span>
                        </a>
                      ) : null}

                      {/* In-App & PWA Push Reminder Action */}
                      <button
                        type="button"
                        onClick={() => handleSendInAppReminder(item)}
                        disabled={sendingUserId === item.fromUser.id}
                        className="ios-tap-target"
                        style={{
                          height: '32px',
                          padding: '0 8px',
                          borderRadius: '10px',
                          background: 'rgba(191, 90, 242, 0.14)',
                          border: '1px solid rgba(191, 90, 242, 0.3)',
                          color: '#bf5af2',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          cursor: sendingUserId === item.fromUser.id ? 'not-allowed' : 'pointer',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          opacity: sendingUserId === item.fromUser.id ? 0.6 : 1
                        }}
                        title={`Send In-App & PWA Push reminder to ${item.fromUser.username}`}
                      >
                        <Bell size={13} />
                        <span>{sendingUserId === item.fromUser.id ? '...' : 'In-App'}</span>
                      </button>

                      {/* WhatsApp Reminder Action */}
                      <a
                        href={generateWhatsAppReminderUrl({
                          name: item.fromUser.full_name || item.fromUser.username,
                          amountOwed: item.amount,
                          creditorName: item.toUser.full_name || item.toUser.username,
                          upiId: toUserUpi || undefined,
                          phone: item.fromUser.whatsapp_phone || undefined
                        })}
                        target="_blank"
                        rel="noreferrer"
                        className="ios-tap-target"
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '10px',
                          background: 'rgba(48, 209, 88, 0.12)',
                          border: '1px solid rgba(48, 209, 88, 0.25)',
                          color: 'var(--ios-green)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textDecoration: 'none'
                        }}
                        title={`Send WhatsApp reminder to ${item.fromUser.username}`}
                      >
                        <Send size={14} />
                      </a>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer Close Button */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          className="ios-primary-button ios-tap-target"
          style={{ width: '100%', padding: '14px', fontSize: '0.92rem', cursor: 'pointer' }}
        >
          Done
        </button>
      </div>
    </GlassModal>
  );
};
